import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { RestrictionsRESTObjForContentApi } from '@confluence/restrictions/entry-points/RestrictionsREST';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { PublishPageAsBlog as PublishPageAsBlogMutation } from './PublishPageAsBlogMutation.experimentalgraphql';
import { handleEditorPublishPageAsBlogError } from './PublishPageAsBlogCleanup';
import { PublishPageAsBlogSources } from './PublishPageAsBlogSourcesEnum';
import type {
	PublishPageAsBlogMutationType,
	PublishPageAsBlogMutationVariables,
	LinkType,
} from './types';

type OnConvertAndPublishSuccessCallback = (data: PublishPageAsBlogMutationType) => void;

type OnPublishFailCallback = ({
	links,
	errorMessage,
	flags,
}: {
	links: LinkType;
	errorMessage: string;
	flags: FlagsStateContainer;
}) => void;

type OnConvertFailCallback = ({
	error,
	flags,
}: {
	error: Error;
	flags: FlagsStateContainer;
}) => void;

type HandlePageToBlogConvert = ({
	flags,
	sourceAction,
	contentId,
	spaceKey,
	message,
	suppressNotifyWatchers,
	schedulePublishDate,
	retrieveEditorTitleAndContent,
	onConvertAndPublishSucceed,
	onPublishFail,
	onConvertFail,
	restrictions,
}: {
	flags: FlagsStateContainer;
	sourceAction: string;
	contentId: string;
	spaceKey: string | null | undefined;
	message?: string;
	suppressNotifyWatchers?: boolean;
	schedulePublishDate?: string;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	onConvertAndPublishSucceed: OnConvertAndPublishSuccessCallback;
	onPublishFail: OnPublishFailCallback;
	onConvertFail: OnConvertFailCallback;
	restrictions?: RestrictionsRESTObjForContentApi;
}) => Promise<void>;

export const useConvertPageToBlog = () => {
	const [publishPageAsBlogMutation] = useMutation<
		PublishPageAsBlogMutationType,
		PublishPageAsBlogMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
		PublishPageAsBlogMutation,
	);

	const handlePageToBlogConversion: HandlePageToBlogConvert = async ({
		flags,
		sourceAction,
		contentId,
		spaceKey,
		message,
		suppressNotifyWatchers,
		schedulePublishDate,
		retrieveEditorTitleAndContent,
		onConvertAndPublishSucceed,
		onPublishFail,
		onConvertFail,
		restrictions,
	}) => {
		const variables = {
			contentId,
			destinationType: 'blogpost',
			sourceStatus: sourceAction === PublishPageAsBlogSources.EditPage ? 'draft' : 'current',
			destinationSpaceKey: spaceKey,
		};

		try {
			if (sourceAction === PublishPageAsBlogSources.EditPage) {
				const { title, adf, syncRev } = await retrieveEditorTitleAndContent();
				handleEditorPublishPageAsBlogError(adf);
				variables['content'] = JSON.stringify(adf);
				variables['title'] = title;
				variables['syncRev'] = syncRev;
				variables['message'] = message;
				variables['suppressNotifyWatchers'] = suppressNotifyWatchers;
				variables['schedulePublishDate'] = schedulePublishDate;
				restrictions && (variables['restrictions'] = restrictions);
			}
			const { data } = await publishPageAsBlogMutation({ variables });

			const hasConvertedAndPublished = data?.experimentalPublishPageAsBlog?.status === 'current';
			const links = data?.experimentalPublishPageAsBlog?._links;
			// it's possible for the conversion to go through but not the publishing of the blog draft
			if (data && hasConvertedAndPublished) {
				onConvertAndPublishSucceed(data);
			} else if (links?.editui && !schedulePublishDate) {
				onPublishFail({
					links,
					errorMessage: 'Page has converted to blog but not published',
					flags,
				});
			}
		} catch (error) {
			onConvertFail({
				error,
				flags,
			});
		}
	};

	return handlePageToBlogConversion;
};
